// The configured owner's existing credentials, presented in the default email tab.
// Account provisioning remains server-side; the phone tab retains one-time-code sign-in.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { UI, retryMinutes, type Lang } from '@ringring/core';
import { api, errorText, type ApiError, type Me } from './api';
import { Filled } from './lang';
import { Icon } from './Icon';

export function CredentialsLogin({ lang, onSignedIn, busy, setBusy }: { lang: Lang; onSignedIn: (me: Me) => void; busy: boolean; setBusy: (busy: boolean) => void }) {
  const t = UI[lang];
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [until, setUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const secret = useRef<HTMLInputElement>(null);
  const locked = now < until;
  useEffect(() => {
    if (!locked) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [locked]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || locked) return;
    setBusy(true);
    setError(null);
    try {
      onSignedIn(await api<Me>('/auth/credentials', { method: 'POST', body: JSON.stringify({ login, password, lang }) }));
    } catch (err) {
      const failure = err as ApiError;
      setError(failure);
      setPassword('');
      if (failure.code === 'code_locked') {
        setNow(Date.now());
        setUntil(Date.now() + (failure.details.retry_after ?? 900) * 1000);
      }
      secret.current?.focus();
    } finally {
      setBusy(false);
    }
  };
  const message = locked ? (
    <Filled template={t.pin_locked} vars={{ minutes: String(retryMinutes(until - now)) }} />
  ) : error?.code === 'code_locked' ? null : error?.status === 404 ? (
    t.signin_unavailable
  ) : error ? (
    errorText(lang, error.code)
  ) : null;

  return (
    <form className="card signin credentials-login" onSubmit={(e) => void submit(e)} aria-busy={busy}>
      <label htmlFor="signin-identifier">{t.signin_identifier}</label>
      <input
        id="signin-identifier"
        type="text"
        inputMode="email"
        autoComplete="username"
        autoCapitalize="none"
        spellCheck={false}
        dir="ltr"
        value={login}
        onChange={(e) => setLogin(e.target.value)}
        readOnly={busy || locked}
        required
      />
      <label htmlFor="signin-secret">{t.signin_secret}</label>
      <input
        ref={secret}
        id="signin-secret"
        type="password"
        autoComplete="current-password"
        dir="ltr"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        readOnly={busy || locked}
        aria-describedby={message ? 'credentials-message' : undefined}
        aria-invalid={error?.code === 'demo_login_failed' || undefined}
        required
      />
      {message ? (
        <p id="credentials-message" role="alert">
          {message}
        </p>
      ) : null}
      <button className="btn" type="submit" disabled={busy || locked}>
        <Icon name="lock" />
        {busy ? t.signin_busy : t.sign_in}
        <Icon name="arrow" className="directional" />
      </button>
    </form>
  );
}
